import {
  getPlatformURL,
  getPlatformURLHash,
} from '@stripe-internal/connect-embedded-lib/dist/utils/getPlatformURL';
import type {ServiceEnvironment} from './getTargetEnvironment';
import {isDevEnvironment} from './isDevEnvironment';

export interface IScriptUrlContext {
  origin: string;
  absoluteFolderPath: string;
  serviceEnvironment: ServiceEnvironment;
  devboxPrefix: string | undefined;
}

export const tryToGuessDevelopmentServiceEnvironmentFromHostPage = (
  hostOrigin: string,
): ServiceEnvironment => {
  const hostname = new URL(hostOrigin).hostname.toLowerCase();
  // dashboard-dev = standard dash
  // connect-dev = express, hosted flows
  if (/(^|\.|--)(connect-dev|dashboard-dev)\.dev\.stripe\.me$/.test(hostname)) {
    return 'turbo';
  }

  if (/\w+-manage\.tunnel\.stripe\.me/.test(hostname)) {
    return 'tunnel';
  }

  // Manage dashboard proxy, manage connect proxy, everything else just gets prod
  return 'prod';
};

export const getServiceEnvironmentFromConnectJsUrl = (
  url: URL,
  hostOrigin: string = new URL(getPlatformURL()).origin,
): ServiceEnvironment => {
  // Example URLs
  // DEVBOX:
  // https://localhost:3001/v0.1/connect.js
  // https://$DEVBOX-USER*-suswebpack.tunnel.stripe.me
  // QA:
  // https://qa-b.stripecdn.com/submerchant-surfaces-statics-srv/assets/v0.1/connect.js
  // https://qa-js.stripe.com/v0.1/connect.js
  // PREPROD:
  // https://preprod-b.stripecdn.com/submerchant-surfaces-statics-srv/assets/v0.1/connect.js
  // https://preprod-connect-js.stripe.com/v0.1/connect.js
  // PROD:
  // https://b.stripecdn.com/submerchant-surfaces-statics-srv/assets/v0.1/connect.js
  // https://connect-js.stripe.com/v0.1/connect.js
  const origin = url.host.toLowerCase();

  if (
    origin.startsWith('localhost') ||
    origin.startsWith('127.0.0.1') ||
    (process.env.USER && origin.includes(String(process.env.USER)))
  ) {
    const guessedEnvironment =
      tryToGuessDevelopmentServiceEnvironmentFromHostPage(hostOrigin);
    // Only log when platformUrl is in the hash — the UI layer frame sets it, but
    // sub-frames (accessory, stripeJs, pdf-preview) do not. Without it,
    // getPlatformURL() falls back to window.location.href and the guessed
    // environment is not meaningful.
    if (getPlatformURLHash()) {
      // eslint-disable-next-line no-console
      console.log(
        `Connect.js will target the environment ${guessedEnvironment} as that is what we matched to your host origin ${hostOrigin}`,
      );
    }
    return guessedEnvironment;
  } else if (origin.startsWith('qa-')) {
    return 'qa';
  } else if (origin.startsWith('preprod-') || origin.startsWith('edge-')) {
    return 'preprod';
  } else {
    return 'prod';
  }
};

/**
 * Extracts the devbox prefix (qualifier) from a connect.js script URL.
 * For example, given `https://jeremyalan-0-el7k--connect_js-mydev.dev.stripe.me`,
 * the devbox prefix is `0-el7k`.
 *
 * The URL format is: `{USER}-{QUALIFIER}--{SERVICE}-{suffix}.dev.stripe.me`
 * This is a best-effort extraction — returns undefined if the pattern doesn't match.
 */
export const parseDevboxPrefixFromScriptUrl = (
  url: URL,
): string | undefined => {
  const hostname = url.hostname.toLowerCase();
  if (!hostname.endsWith('.dev.stripe.me')) {
    return undefined;
  }

  // Strip the .dev.stripe.me suffix and extract the subdomain part
  const subdomain = hostname.slice(0, -'.dev.stripe.me'.length);

  // The pattern is USER-QUALIFIER--SERVICE-suffix
  // We capture the qualifier as a digit sequence followed by alphanumeric chars (e.g. 0-el7k)
  const match = subdomain.match(/(\d+-[a-z0-9]+)--/);
  if (!match) {
    return undefined;
  }

  return match[1];
};

let result: IScriptUrlContext | undefined;
export const computeCurrentScriptUrlContext = (
  // eslint-disable-next-line @stripe-internal/embedded/no-restricted-globals
  currentScript: HTMLOrSVGScriptElement | null = document.currentScript,
  isDevEnv: boolean = isDevEnvironment,
) => {
  if (result && !currentScript) {
    return;
  }

  const currentScriptUrl = currentScript?.getAttribute('src');
  if (!currentScriptUrl) {
    return;
  }

  // Example: connect JS is https://qa-b.stripecdn.com/submerchant-surfaces-statics-srv/assets/connect.js
  // then origin is https://qa-b.stripecdn.com and folder path is https://qa-b.stripecdn.com/submerchant-surfaces-statics-srv/assets/

  const url = new URL(
    currentScriptUrl,
    // If we are in an iframe, a relative script path is used, i.e. /ui_layer.js, hence we need to use the window.location.href as a base URL.
    // Also, in the sample platform, we use a relative script path, i.e. /v0.1/connect.js
    // eslint-disable-next-line @stripe-internal/embedded/no-restricted-globals
    window.location.href,
  );
  const folderPathComponents = url.pathname.split('/');
  folderPathComponents.pop(); // Remove the connect.js filename
  // In we are running in iframes, the script we are executing is not the connect.js script, but the iframe's script
  // e.g. https://b.stripecdn.submerchant-surfaces-statics-srv/assets/ui_layer.js or https://connect-js.stripe.com/ui_layer.js
  // Therefore, there is no connect.js version to remove from the folder path - we also don't want to remove the /assets path
  // if it exists. Hence, we add a check for the version path here

  const isLastPathSegmentVersion =
    folderPathComponents[folderPathComponents.length - 1].match(/^v\d+\.\d+$/i);

  if (isLastPathSegmentVersion) {
    folderPathComponents.pop(); // Remove the connect.js version if present
  }

  const folderPath = folderPathComponents.join('/');
  let {origin} = url;
  if (isDevEnv) {
    // When running locally, we intentionally replace the data layer URL with 127.0.0.1 to force different origins between the
    // host and the data layer. This helps to catch issues stemming from differing origins between the host and data layer earlier
    // in the development process (makes local dev ~= production)
    origin = url.origin.replace('localhost', '127.0.0.1');
  }

  const serviceEnvironment = getServiceEnvironmentFromConnectJsUrl(url);
  const devboxPrefix = parseDevboxPrefixFromScriptUrl(url);

  result = {
    origin,
    absoluteFolderPath: origin + folderPath,
    serviceEnvironment,
    devboxPrefix,
  };
};
// eslint-disable-next-line @stripe-internal/embedded/no-restricted-globals
computeCurrentScriptUrlContext(document.currentScript);

export const getCurrentScriptUrlContext = () => {
  if (!result) {
    throw new Error('Script location was not computed');
  }
  return result;
};
